import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../../../context/CartContext';
import {
  FaArrowRight,
  FaCheck,
  FaClock,
  FaRedo,
  FaTimes,
} from 'react-icons/fa';
import { api, ApiError } from '../../../api/client';
import { cancelPendingOrder } from '../../../api/ordersApi';
import { API_BASE_URL } from '../../../config/api';
import AccountSessionError from '../AccountSessionError';
import {
  clearStoredAuth,
  clearStoredAuthIfApiError,
  DIGIRASA_AUTH_UPDATED_EVENT,
  extractApiErrorMessage,
  readStoredAuth,
  SIGN_IN_REQUIRED_MESSAGE,
} from '../profileFormUtils';
import {
  extractOrdersArray,
  formatOrderAmountInr,
  formatOrderReferenceDateLine,
  getLivePillClassName,
  getOrderStatusDisplayLabel,
  getStatusVisualKind,
  getOrderStatusString,
  getOrderCardBrandName,
  getOrderCardLocation,
  getOrderItemsCommaList,
  getPastCardSubtitle,
  getOrderSpecialInstructions,
  getPrimaryOrderTitle,
  isSameOrderRow,
  normalizeOrderRow,
  orderIsSubscriptionListItem,
  orderLineItemsToCartItems,
  partitionOrdersForMainTab,
  shouldShowCancelPendingOrder,
  shouldShowCompletePayment,
  getNearestCompletePaymentWindowExpiryMs,
  isPaymentPendingStatus,
  shouldShowOrderHelp,
  shouldShowTrackOrderOnList,
  sortOrdersByDateDesc,
} from './accountOrdersUtils';
import OrderDetailsSidePane, { ACCOUNT_ORDERS_DETAIL_PANE_ID } from './OrderDetailsSidePane';
import AccountMyOrdersLoadingSkeleton from './AccountMyOrdersLoadingSkeleton';
import OrderHelpDialog from './OrderHelpDialog';
import OrderProgressStepper from './OrderProgressStepper';
import {
  buildPendingOrderPaymentFromOrderRow,
  clearStoredPendingOrderPayment,
  persistPendingOrderPayment,
  readStoredPendingOrderPayment,
} from '../Payment/pendingOrderPaymentState';
import { clearPaymentFailure } from '../Payment/paymentFailureState';
import '../AccountLayout.css';
import './AccountMyOrders.css';

function ordersLoadMessage(err) {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Please sign in again to view your orders.';
    if (err.code === 'TIMEOUT') return err.message;
    if (err.code === 'NETWORK') return err.message;
    const fromDetails = extractApiErrorMessage(err.details);
    if (fromDetails) return fromDetails;
    if (err.message && !/^Request failed \(\d+\)$/.test(err.message)) return err.message;
    if (err.status === 404) return 'Orders could not be found. Try again later.';
    if (err.status >= 500) return 'Something went wrong on our side. Please try again in a moment.';
  }
  return 'Could not load your orders. Please try again.';
}

function cancelOrderMessage(err) {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Please sign in again to cancel this order.';
    const fromDetails = extractApiErrorMessage(err.details);
    if (fromDetails) return fromDetails;
    if (err.message && !/^Request failed \(\d+\)$/.test(err.message)) return err.message;
    if (err.status === 403) return 'You are not allowed to cancel this order.';
    if (err.status === 404) return 'Order not found. It may have already been updated.';
    if (err.status >= 500) return 'Something went wrong on our side. Please try again in a moment.';
  }
  return 'Could not cancel this order. Please try again.';
}

const EMPTY_ORDER_ROWS = [];
const ORDERS_SIGN_IN_PATH = '/account/orders';

function ReorderConfirmDialog({ open, cartCount, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="account-orders-reorder-dialog" role="presentation">
      <button type="button" className="account-orders-reorder-dialog__backdrop" aria-label="Close" onClick={onCancel} />
      <div className="account-orders-reorder-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="account-orders-reorder-title">
        <h2 id="account-orders-reorder-title" className="account-orders-reorder-dialog__title">
          Replace cart items?
        </h2>
        <p className="account-orders-reorder-dialog__text">
          You have {cartCount} {cartCount === 1 ? 'item' : 'items'} in your cart. Reordering will remove them and add
          the items from this order instead.
        </p>
        <div className="account-orders-reorder-dialog__actions">
          <button
            type="button"
            className="account-orders-reorder-dialog__btn account-orders-reorder-dialog__btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="account-orders-reorder-dialog__btn account-orders-reorder-dialog__btn--primary"
            onClick={onConfirm}
          >
            OK, replace cart
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelPendingOrderDialog({ open, orderNumber, cancelling, error, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="account-orders-reorder-dialog" role="presentation">
      <button type="button" className="account-orders-reorder-dialog__backdrop" aria-label="Close" onClick={onCancel} />
      <div
        className="account-orders-reorder-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-orders-cancel-title"
      >
        <h2 id="account-orders-cancel-title" className="account-orders-reorder-dialog__title">
          Cancel order?
        </h2>
        <p className="account-orders-reorder-dialog__text">
          Cancel order #{orderNumber}? No payment was taken. This cannot be undone.
        </p>
        {error ? (
          <p className="account-orders-reorder-dialog__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="account-orders-reorder-dialog__actions">
          <button
            type="button"
            className="account-orders-reorder-dialog__btn account-orders-reorder-dialog__btn--ghost"
            onClick={onCancel}
            disabled={cancelling}
          >
            Keep order
          </button>
          <button
            type="button"
            className="account-orders-reorder-dialog__btn account-orders-reorder-dialog__btn--danger"
            onClick={onConfirm}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling…' : 'Cancel Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'past', label: 'Past' },
];

function isDefaultDigiRasaBrand(name) {
  return String(name || '')
    .trim()
    .toLowerCase() === 'digirasa';
}

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];
const PAGE_SIZE_STORAGE_KEY = 'digirasa_my_orders_page_size';

function readStoredPageSize() {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    if (PAGE_SIZE_OPTIONS.includes(n)) return n;
  } catch {
    /* ignore */
  }
  return 10;
}

/** @param {{ id: string; displayId: string; status: string; orderType: string; total: number | null; placedAt: Date | null; raw: Record<string, unknown> }} o */
function LiveOrderCard({ o, detailOrder, setDetailOrder, onOpenHelp, onRequestCancel, retryWindowTick }) {
  const navigate = useNavigate();
  const open = isSameOrderRow(detailOrder, o);
  const status = getOrderStatusString(o);
  const badgeLabel = getOrderStatusDisplayLabel(status);
  const pillClassName = getLivePillClassName(status);
  void retryWindowTick;
  const showPay = shouldShowCompletePayment(o);
  const showCancel = shouldShowCancelPendingOrder(status);
  const showTrack = shouldShowTrackOrderOnList(status);
  const brand = getOrderCardBrandName(o.raw);
  const location = getOrderCardLocation(o.raw);
  const metaIdLine = formatOrderReferenceDateLine(o.displayId, o.placedAt);
  const headingMain = !isDefaultDigiRasaBrand(brand)
    ? brand
    : getPrimaryOrderTitle(o.raw, o.orderType);
  const itemsLine =
    getOrderItemsCommaList(o.raw) ||
    getPastCardSubtitle(o.raw) ||
    getPrimaryOrderTitle(o.raw, o.orderType) ||
    '';
  const specialInstructions = getOrderSpecialInstructions(o.raw);
  const liveAmount = formatOrderAmountInr(o.total);
  const showHelp = shouldShowOrderHelp(o);

  const handleCompletePayment = () => {
    const pendingOrderPayment = buildPendingOrderPaymentFromOrderRow(o);
    if (!pendingOrderPayment) return;
    persistPendingOrderPayment(pendingOrderPayment);
    navigate('/payment', { state: { pendingOrderPayment } });
  };

  return (
    <li className="account-orders__card account-orders__card--live-v2 account-orders__card--food">
      <div className="account-orders__food-card__header">
        <div className="account-orders__food-card__main">
          {headingMain ? <div className="account-orders__food-card__brand">{headingMain}</div> : null}
          {location ? <div className="account-orders__food-card__loc">{location}</div> : null}
          <div className="account-orders__food-card__meta-id">{metaIdLine}</div>
          <button
            type="button"
            className="account-orders__food-card__details-link"
            aria-expanded={open}
            aria-controls={ACCOUNT_ORDERS_DETAIL_PANE_ID}
            onClick={() => setDetailOrder((cur) => (isSameOrderRow(cur, o) ? null : o))}
          >
            View details
          </button>
        </div>
        <div className="account-orders__food-card__status account-orders__food-card__status--live">
          <span className={`account-orders__food-card__pill account-orders__pill ${pillClassName}`}>
            <FaClock className="account-orders__pill-icon" aria-hidden />
            {badgeLabel}
          </span>
        </div>
      </div>

      <div className="account-orders__food-card__divider" aria-hidden />

      <div className="account-orders__food-card__items-row">
        <div className="account-orders__food-card__items-col">
          <p className="account-orders__food-card__items">
            {itemsLine || (
              <span className="account-orders__food-card__items--muted">Items will appear here when available.</span>
            )}
          </p>
          {specialInstructions ? (
            <p className="account-orders__food-card__notes">
              <span>Special instructions:</span> {specialInstructions}
            </p>
          ) : null}
        </div>
        <div className="account-orders__food-card__total-block">
          <span className="account-orders__food-card__total-line">
            <span className="account-orders__food-card__total-label">Total:</span>{' '}
            <span
              className={
                liveAmount
                  ? 'account-orders__food-card__total-amt'
                  : 'account-orders__food-card__total-amt account-orders__food-card__total-amt--muted'
              }
            >
              {liveAmount || '—'}
            </span>
          </span>
        </div>
      </div>

      <OrderProgressStepper status={status} />

      <div className="account-orders__food-card__footer account-orders__food-card__footer--live">
        <div className="account-orders__food-card__footer-actions">
          {showPay ? (
            <button
              type="button"
              className="account-orders__btn account-orders__btn--menu-primary account-orders__btn--compact"
              onClick={handleCompletePayment}
            >
              Complete payment
              <FaArrowRight className="account-orders__btn-arrow" aria-hidden />
            </button>
          ) : null}
          {showCancel ? (
            <button
              type="button"
              className="account-orders__btn account-orders__btn--menu-cancel account-orders__btn--compact"
              onClick={() => onRequestCancel(o)}
            >
              <FaTimes className="account-orders__btn-cancel-icon" aria-hidden />
              Cancel Order
            </button>
          ) : null}
          {showTrack ? (
            <button
              type="button"
              className="account-orders__btn account-orders__btn--menu-primary account-orders__btn--compact"
              aria-controls={ACCOUNT_ORDERS_DETAIL_PANE_ID}
              onClick={() => setDetailOrder(o)}
            >
              Track order
              <FaArrowRight className="account-orders__btn-arrow" aria-hidden />
            </button>
          ) : null}
          {showHelp ? (
            <button
              type="button"
              className="account-orders__btn account-orders__btn--menu-help account-orders__btn--compact"
              onClick={() => onOpenHelp(o)}
            >
              Help
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/** @param {{ id: string; displayId: string; status: string; orderType: string; total: number | null; placedAt: Date | null; raw: Record<string, unknown> }} o */
function PastOrderCard({ o, detailOrder, setDetailOrder, onOpenHelp }) {
  const navigate = useNavigate();
  const { cartItems, replaceCart } = useCart();
  const [reorderConfirmOpen, setReorderConfirmOpen] = useState(false);
  const open = isSameOrderRow(detailOrder, o);
  const amountLabel = formatOrderAmountInr(o.total);
  const brand = getOrderCardBrandName(o.raw);
  const location = getOrderCardLocation(o.raw);
  const metaIdLine = formatOrderReferenceDateLine(o.displayId, o.placedAt);
  const headingMain = !isDefaultDigiRasaBrand(brand)
    ? brand
    : getPrimaryOrderTitle(o.raw, o.orderType);
  const itemsLine = getOrderItemsCommaList(o.raw) || getPastCardSubtitle(o.raw) || '';
  const specialInstructions = getOrderSpecialInstructions(o.raw);
  const status = getOrderStatusString(o);
  const statusLabel = getOrderStatusDisplayLabel(status);
  const statusKind = getStatusVisualKind(status);
  const hideReorder = orderIsSubscriptionListItem(o);
  const showHelp = shouldShowOrderHelp(o);
  const totalPaidLabel = statusKind === 'cancelled' ? 'Total' : 'Total paid';
  const reorderCartItems = useMemo(() => orderLineItemsToCartItems(o.raw), [o.raw]);
  const canReorder = !hideReorder && reorderCartItems.length > 0;

  const applyReorderToCart = useCallback(() => {
    replaceCart(reorderCartItems);
    setReorderConfirmOpen(false);
    navigate('/cart');
  }, [navigate, replaceCart, reorderCartItems]);

  const handleReorderClick = useCallback(() => {
    if (!reorderCartItems.length) return;
    if (cartItems.length > 0) {
      setReorderConfirmOpen(true);
      return;
    }
    applyReorderToCart();
  }, [applyReorderToCart, cartItems.length, reorderCartItems.length]);

  return (
    <li className="account-orders__card account-orders__card--past-v2 account-orders__card--food">
      <div className="account-orders__food-card__header">
        <div className="account-orders__food-card__main">
          {headingMain ? <div className="account-orders__food-card__brand">{headingMain}</div> : null}
          {location ? <div className="account-orders__food-card__loc">{location}</div> : null}
          <div className="account-orders__food-card__meta-id">{metaIdLine}</div>
          <button
            type="button"
            className="account-orders__food-card__details-link"
            aria-expanded={open}
            aria-controls={ACCOUNT_ORDERS_DETAIL_PANE_ID}
            onClick={() => setDetailOrder((cur) => (isSameOrderRow(cur, o) ? null : o))}
          >
            View details
          </button>
        </div>
        <div className="account-orders__food-card__status account-orders__food-card__status--past">
          <span className="account-orders__food-card__status-text">{statusLabel}</span>
          {statusKind === 'delivered' ? (
            <span className="account-orders__food-card__status-dot" aria-hidden>
              <FaCheck className="account-orders__food-card__status-check" />
            </span>
          ) : null}
          {statusKind === 'cancelled' ? (
            <span className="account-orders__food-card__status-dot account-orders__food-card__status-dot--cancel" aria-hidden>
              <FaTimes className="account-orders__food-card__status-x" />
            </span>
          ) : null}
        </div>
      </div>

      <div className="account-orders__food-card__divider" aria-hidden />

      <div className="account-orders__food-card__items-row">
        <div className="account-orders__food-card__items-col">
          <p className="account-orders__food-card__items">
            {itemsLine || (
              <span className="account-orders__food-card__items--muted">Items will appear here when available.</span>
            )}
          </p>
          {specialInstructions ? (
            <p className="account-orders__food-card__notes">
              <span>Special instructions:</span> {specialInstructions}
            </p>
          ) : null}
        </div>
        <div className="account-orders__food-card__total-block">
          <span className="account-orders__food-card__total-line">
            <span className="account-orders__food-card__total-label">
              {totalPaidLabel}
              {amountLabel ? ':' : ''}
            </span>{' '}
            <span
              className={
                amountLabel
                  ? 'account-orders__food-card__total-amt'
                  : 'account-orders__food-card__total-amt account-orders__food-card__total-amt--muted'
              }
            >
              {amountLabel || '—'}
            </span>
          </span>
        </div>
      </div>

      <div className="account-orders__food-card__footer">
        <div className="account-orders__food-card__footer-actions">
          {canReorder ? (
            <button
              type="button"
              className="account-orders__btn account-orders__btn--menu-primary account-orders__btn--compact"
              onClick={handleReorderClick}
            >
              Reorder
            </button>
          ) : null}
          {showHelp ? (
            <button
              type="button"
              className={`account-orders__btn account-orders__btn--menu-help account-orders__btn--compact${!canReorder ? ' account-orders__btn--compact--solo' : ''}`}
              onClick={() => onOpenHelp(o)}
            >
              Help
            </button>
          ) : null}
        </div>
      </div>

      <ReorderConfirmDialog
        open={reorderConfirmOpen}
        cartCount={cartItems.reduce((sum, i) => sum + i.quantity, 0)}
        onCancel={() => setReorderConfirmOpen(false)}
        onConfirm={applyReorderToCart}
      />
    </li>
  );
}

const AccountMyOrders = () => {
  const [searchParams] = useSearchParams();
  const [fetching, setFetching] = useState(true);
  const [hasCompletedFetch, setHasCompletedFetch] = useState(false);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [detailOrder, setDetailOrder] = useState(null);
  const [helpOrder, setHelpOrder] = useState(null);
  const [cancelConfirmOrder, setCancelConfirmOrder] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [tab, setTab] = useState('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => readStoredPageSize());
  const [retryWindowTick, setRetryWindowTick] = useState(0);

  const loadOrders = useCallback(async () => {
    const { token } = readStoredAuth();
    if (!token) {
      clearStoredAuth();
      setError(SIGN_IN_REQUIRED_MESSAGE);
      setOrders([]);
      setFetching(false);
      setHasCompletedFetch(true);
      return;
    }

    setFetching(true);
    setError('');
    try {
      const url = `${API_BASE_URL}/orders/orderPerUser`;
      const { data } = await api.get(url, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        timeoutMs: 20000,
      });
      const rows = extractOrdersArray(data).map((row) => normalizeOrderRow(row));
      setOrders(sortOrdersByDateDesc(rows));
      setDetailOrder(null);
    } catch (err) {
      setOrders([]);
      clearStoredAuthIfApiError(err);
      setError(ordersLoadMessage(err));
      setDetailOrder(null);
    } finally {
      setFetching(false);
      setHasCompletedFetch(true);
    }
  }, []);

  const { live, past } = useMemo(() => partitionOrdersForMainTab(orders), [orders]);
  const tabCounts = useMemo(
    () => ({
      all: live.length + past.length,
      active: live.length,
      past: past.length,
    }),
    [live.length, past.length],
  );
  const onlySubscriptions =
    orders.length > 0 && live.length === 0 && past.length === 0;

  const visibleLive = useMemo(
    () => (tab === 'past' || onlySubscriptions ? EMPTY_ORDER_ROWS : live),
    [tab, onlySubscriptions, live],
  );
  const visiblePast = useMemo(
    () => (tab === 'active' || onlySubscriptions ? EMPTY_ORDER_ROWS : past),
    [tab, onlySubscriptions, past],
  );

  const ordersSequence = useMemo(() => {
    if (onlySubscriptions) return [];
    if (tab === 'past') return visiblePast.map((order) => ({ kind: 'past', order }));
    if (tab === 'active') return visibleLive.map((order) => ({ kind: 'live', order }));
    return [
      ...visibleLive.map((order) => ({ kind: 'live', order })),
      ...visiblePast.map((order) => ({ kind: 'past', order })),
    ];
  }, [tab, onlySubscriptions, visibleLive, visiblePast]);

  const totalFiltered = ordersSequence.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize) || 1);

  const pagedSlice = useMemo(() => {
    const start = (page - 1) * pageSize;
    return ordersSequence.slice(start, start + pageSize);
  }, [ordersSequence, page, pageSize]);

  const pagedGroups = useMemo(() => {
    /** @type {{ kind: string; orders: (typeof ordersSequence)[0]['order'][] }[]} */
    const groups = [];
    for (const entry of pagedSlice) {
      const last = groups[groups.length - 1];
      if (last && last.kind === entry.kind) {
        last.orders.push(entry.order);
      } else {
        groups.push({ kind: entry.kind, orders: [entry.order] });
      }
    }
    return groups;
  }, [pagedSlice]);

  const openOrderHelp = useCallback((order) => {
    setHelpOrder(order);
  }, []);

  const requestCancelOrder = useCallback((order) => {
    setCancelError('');
    setCancelConfirmOrder(order);
  }, []);

  const dismissCancelDialog = useCallback(() => {
    if (cancelling) return;
    setCancelConfirmOrder(null);
    setCancelError('');
  }, [cancelling]);

  const handleConfirmCancelOrder = useCallback(async () => {
    if (!cancelConfirmOrder) return;
    const orderToCancel = cancelConfirmOrder;
    const { token } = readStoredAuth();
    if (!token) {
      setCancelError(SIGN_IN_REQUIRED_MESSAGE);
      return;
    }

    setCancelling(true);
    setCancelError('');
    try {
      await cancelPendingOrder({ token, orderNumber: orderToCancel.displayId });
      const stored = readStoredPendingOrderPayment();
      if (stored?.orderNumber === orderToCancel.displayId) {
        clearStoredPendingOrderPayment();
      }
      clearPaymentFailure(orderToCancel.displayId);
      setCancelConfirmOrder(null);
      setDetailOrder((cur) => (isSameOrderRow(cur, orderToCancel) ? null : cur));
      await loadOrders();
    } catch (err) {
      setCancelError(cancelOrderMessage(err));
      // Status may have changed since the list was loaded (e.g. payment completed).
      await loadOrders();
    } finally {
      setCancelling(false);
    }
  }, [cancelConfirmOrder, loadOrders]);

  const tabEmptyMessage =
    tab === 'active'
      ? 'No active orders right now.'
      : tab === 'past'
        ? 'No past orders in this view.'
        : null;

  useEffect(() => {
    setPage(1);
    setDetailOrder(null);
  }, [tab, pageSize]);

  useEffect(() => {
    setDetailOrder(null);
  }, [page]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'past' || t === 'active' || t === 'all') {
      setTab(t);
    }
  }, [searchParams]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const pendingOrders = live.filter((o) => isPaymentPendingStatus(getOrderStatusString(o)));
    const nearestMs = getNearestCompletePaymentWindowExpiryMs(pendingOrders);
    if (nearestMs == null) return undefined;
    const timer = window.setTimeout(() => {
      setRetryWindowTick((tick) => tick + 1);
    }, nearestMs + 50);
    return () => window.clearTimeout(timer);
  }, [live, retryWindowTick]);

  useEffect(() => {
    const onAuth = () => loadOrders();
    window.addEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
    return () => window.removeEventListener(DIGIRASA_AUTH_UPDATED_EVENT, onAuth);
  }, [loadOrders]);

  const showTabs = !error && orders.length > 0;
  const showEmptyTab =
    showTabs &&
    ((tab === 'active' && visibleLive.length === 0) ||
      (tab === 'past' && visiblePast.length === 0));

  const showPaginationControls = showTabs && !onlySubscriptions && !showEmptyTab && totalFiltered > 0;
  const isInitialLoad = fetching && !hasCompletedFetch;

  return (
    <div className="account-page account-page--orders-redesign" aria-busy={isInitialLoad}>
      <div className="account-orders__toolbar">
        <h1 className="account-orders__page-title">My orders</h1>
        <button
          type="button"
          className="account-orders__refresh account-orders__refresh--icon"
          onClick={loadOrders}
          disabled={fetching}
          aria-label={fetching ? 'Refreshing orders' : 'Refresh orders'}
        >
          <FaRedo className={fetching ? 'account-orders__refresh-svg--spin' : ''} aria-hidden />
        </button>
      </div>
      <p className="account-page__muted account-page__muted--orders-tight">
        Track food orders here. Bulk catering requests are under{' '}
        <Link to="/account/bulk-orders">Bulk Orders</Link>.
      </p>

      {isInitialLoad ? (
        <>
          <AccountMyOrdersLoadingSkeleton />
          <p className="visually-hidden" aria-live="polite">
            Loading your orders…
          </p>
        </>
      ) : null}

      {!isInitialLoad && error ? (
        <AccountSessionError
          message={error}
          returnPath={ORDERS_SIGN_IN_PATH}
          signInSuffix="to view your orders"
          onRetry={loadOrders}
          className="account-orders__error-block"
        />
      ) : null}

      {!isInitialLoad && !error && orders.length === 0 ? (
        <div className="account-orders__empty">
          <p>You do not have any orders to show yet.</p>
          <Link to="/our-menu" className="account-orders__cta">
            Browse our menu
          </Link>
        </div>
      ) : null}

      {!isInitialLoad && showTabs ? (
        <div className="account-orders__tabs" role="tablist" aria-label="Order filters">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              aria-label={`${t.label}, ${tabCounts[t.id]} orders`}
              className={`account-orders__tab${tab === t.id ? ' account-orders__tab--selected' : ''}`}
              onClick={() => {
                setTab(t.id);
                setDetailOrder(null);
              }}
            >
              <span className="account-orders__tab__label">{t.label}</span>
              <span className="account-orders__tab__badge" aria-hidden>
                {tabCounts[t.id]}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {!isInitialLoad && showEmptyTab ? (
        <p className="account-orders__tab-empty">{tabEmptyMessage}</p>
      ) : null}

      {!isInitialLoad && onlySubscriptions ? (
        <p className="account-orders__tab-empty">
          Subscription-only activity is available in the DigiRasa app. No regular orders to show here
          yet.
        </p>
      ) : null}

      {!isInitialLoad && !error && orders.length > 0 ? (
        <>
          {showPaginationControls ? (
            <nav className="account-orders__pagination-toolbar" aria-label="Order list pagination">
              <p className="account-orders__pagination-range">
                {totalFiltered === 0
                  ? 'Showing 0 orders'
                  : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalFiltered)} of ${totalFiltered} orders`}
              </p>
              <label className="account-orders__page-size-label">
                <span className="account-orders__page-size-text">Per page</span>
                <select
                  className="account-orders__page-size-select"
                  value={pageSize}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (!PAGE_SIZE_OPTIONS.includes(v)) return;
                    setPageSize(v);
                    try {
                      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(v));
                    } catch {
                      /* ignore */
                    }
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </nav>
          ) : null}

          <div className="account-orders__sections" aria-busy={fetching}>
            {(() => {
              let base = (page - 1) * pageSize;
              return pagedGroups.map((group, gi) => {
                const headingId = `account-orders-${group.kind}-kicker-${page}-${gi}`;
                const title = group.kind === 'live' ? 'Live' : 'Past orders';
                const section = (
                  <section
                    key={`${group.kind}-${gi}-${page}`}
                    className="account-orders__section"
                    aria-labelledby={headingId}
                  >
                    <h2 id={headingId} className="account-orders__section-kicker">
                      {title}
                    </h2>
                    <ul className="account-orders__list">
                      {group.orders.map((o, idx) => {
                        const ri = base + idx;
                        const k = o.id ? `order-${o.id}` : `order-${group.kind}-${page}-${ri}`;
                        return group.kind === 'live' ? (
                          <LiveOrderCard
                            key={k}
                            o={o}
                            detailOrder={detailOrder}
                            setDetailOrder={setDetailOrder}
                            onOpenHelp={openOrderHelp}
                            onRequestCancel={requestCancelOrder}
                            retryWindowTick={retryWindowTick}
                          />
                        ) : (
                          <PastOrderCard
                            key={k}
                            o={o}
                            detailOrder={detailOrder}
                            setDetailOrder={setDetailOrder}
                            onOpenHelp={openOrderHelp}
                          />
                        );
                      })}
                    </ul>
                  </section>
                );
                base += group.orders.length;
                return section;
              });
            })()}
          </div>

          {showPaginationControls && totalPages > 1 ? (
            <nav className="account-orders__pagination-nav" aria-label="Order pages">
              <button
                type="button"
                className="account-orders__page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="account-orders__page-status">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="account-orders__page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      ) : null}

      <OrderDetailsSidePane
        order={detailOrder}
        isOpen={detailOrder != null}
        onClose={() => setDetailOrder(null)}
        onCancelOrder={requestCancelOrder}
        cancelling={cancelling}
      />

      <CancelPendingOrderDialog
        open={cancelConfirmOrder != null}
        orderNumber={cancelConfirmOrder?.displayId || ''}
        cancelling={cancelling}
        error={cancelError}
        onCancel={dismissCancelDialog}
        onConfirm={handleConfirmCancelOrder}
      />

      <OrderHelpDialog open={helpOrder != null} order={helpOrder} onClose={() => setHelpOrder(null)} />
    </div>
  );
};

export default AccountMyOrders;
